import * as Sentry from "@sentry/react";
import sileroVadModelUrl from "@ricky0123/vad-web/dist/silero_vad_legacy.onnx";
import dnsmosModelUrl from "webmos/models/sig_bak_ovr.onnx";
import {
  MIN_MOS_BAK,
  MIN_MOS_OVR,
  MIN_MOS_SIG,
  MIN_RMS_DB,
  MIN_SPEECH_DURATION_SEC,
  MIN_SPEECH_RATIO,
  getAudioQualityThresholds,
  getOrtWasmBase,
} from "./constants";

let vadInstancePromise = null;
let dnsmosInitPromise = null;

const DB_FLOOR = 1e-10;

function linearToDb(value) {
  return 20 * Math.log10(Math.max(value, DB_FLOOR));
}

async function getVadInstance() {
  if (!vadInstancePromise) {
    vadInstancePromise = (async () => {
      const { NonRealTimeVAD } = await import("@ricky0123/vad-web");

      return NonRealTimeVAD.new({
        modelURL: sileroVadModelUrl,
        modelFetcher: async (path) => {
          const response = await fetch(path);
          if (!response.ok) {
            throw new Error(`Failed to fetch VAD model: ${response.status} ${path}`);
          }
          return response.arrayBuffer();
        },
        ortConfig: (ort) => {
          ort.env.wasm.wasmPaths = getOrtWasmBase();
          ort.env.logLevel = "error";
        },
      });
    })();
  }
  return vadInstancePromise;
}

async function initDnsmos() {
  if (!dnsmosInitPromise) {
    dnsmosInitPromise = (async () => {
      const { initDNSMOS } = await import("webmos");
      await initDNSMOS({
        modelUrl: dnsmosModelUrl,
        wasmPaths: getOrtWasmBase(),
        ortLogLevel: "error",
      });
    })();
  }
  return dnsmosInitPromise;
}

export async function decodeAudioBlob(blob) {
  const { utils } = await import("@ricky0123/vad-web");
  return utils.audioFileToArray(blob);
}

export function runAmplitudeCheck(audio) {
  let sumSquares = 0;
  let peak = 0;

  for (let i = 0; i < audio.length; i += 1) {
    const sample = audio[i];
    sumSquares += sample * sample;
    peak = Math.max(peak, Math.abs(sample));
  }

  const rms = audio.length > 0 ? Math.sqrt(sumSquares / audio.length) : 0;
  const rmsDb = linearToDb(rms);
  const peakDb = linearToDb(peak);

  return {
    rms_db: rmsDb,
    peak_db: peakDb,
    has_audio: rmsDb >= MIN_RMS_DB,
  };
}

export async function runSileroVad(audio, sampleRate) {
  const vad = await getVadInstance();
  let speechDurationMs = 0;
  let segmentCount = 0;

  for await (const segment of vad.run(audio, sampleRate)) {
    speechDurationMs += Math.max(0, segment.end - segment.start);
    segmentCount += 1;
  }

  const totalDurationSec = audio.length / sampleRate;
  const speechDurationSec = speechDurationMs / 1000;
  const speechRatio =
    totalDurationSec > 0 ? speechDurationSec / totalDurationSec : 0;

  return {
    speech_duration_sec: speechDurationSec,
    speech_ratio: speechRatio,
    segment_count: segmentCount,
    has_speech:
      speechDurationSec >= MIN_SPEECH_DURATION_SEC &&
      speechRatio >= MIN_SPEECH_RATIO,
  };
}

export async function runDnsmos(audio, sampleRate) {
  await initDnsmos();
  const { runDNSMOS } = await import("webmos");
  const result = await runDNSMOS(audio, sampleRate);

  return {
    mos_sig: result.mos_sig,
    mos_bak: result.mos_bak,
    mos_ovr: result.mos_ovr,
    is_speech_clear:
      result.mos_sig >= MIN_MOS_SIG &&
      result.mos_bak >= MIN_MOS_BAK &&
      result.mos_ovr >= MIN_MOS_OVR,
  };
}

export function computeQualityPassed(audioQuality) {
  return (
    audioQuality.has_audio === true &&
    audioQuality.has_speech === true &&
    audioQuality.is_speech_clear === true
  );
}

function buildFailReason(audioQuality) {
  if (audioQuality.has_audio !== true) {
    return "silence";
  }
  if (audioQuality.has_speech !== true) {
    return "no_speech";
  }
  if (audioQuality.is_speech_clear !== true) {
    return "noise";
  }
  return null;
}

export async function analyzeAudioQuality(blob) {
  const thresholds = getAudioQualityThresholds();

  try {
    const { audio, sampleRate } = await decodeAudioBlob(blob);
    const amplitude = runAmplitudeCheck(audio);

    const audioQuality = {
      has_audio: amplitude.has_audio,
      has_speech: null,
      is_speech_clear: null,
    };

    const audioQualityRaw = {
      thresholds,
      amplitude: {
        rms_db: amplitude.rms_db,
        peak_db: amplitude.peak_db,
      },
      speech_detection: null,
      speech_quality: null,
    };

    if (amplitude.has_audio) {
      const speechDetection = await runSileroVad(audio, sampleRate);
      audioQuality.has_speech = speechDetection.has_speech;
      audioQualityRaw.speech_detection = {
        speech_duration_sec: speechDetection.speech_duration_sec,
        speech_ratio: speechDetection.speech_ratio,
        segment_count: speechDetection.segment_count,
      };

      if (speechDetection.has_speech) {
        const speechQuality = await runDnsmos(audio, sampleRate);
        audioQuality.is_speech_clear = speechQuality.is_speech_clear;
        audioQualityRaw.speech_quality = {
          mos_sig: speechQuality.mos_sig,
          mos_bak: speechQuality.mos_bak,
          mos_ovr: speechQuality.mos_ovr,
        };
      }
    }

    const passed = computeQualityPassed(audioQuality);

    return {
      passed,
      failReason: passed ? null : buildFailReason(audioQuality),
      audio_quality: audioQuality,
      audio_quality_raw: audioQualityRaw,
    };
  } catch (error) {
    console.error("Audio quality analysis failed:", error);
    Sentry.captureException(error);
    return {
      passed: true,
      failReason: null,
      error: error?.message || "unknown",
      audio_quality: {
        has_audio: null,
        has_speech: null,
        is_speech_clear: null,
      },
      audio_quality_raw: {
        thresholds,
        amplitude: { rms_db: null, peak_db: null },
        speech_detection: null,
        speech_quality: null,
      },
    };
  }
}

export async function preloadAudioQualityModels() {
  try {
    await Promise.all([getVadInstance(), initDnsmos()]);
  } catch (error) {
    console.warn("Failed to preload audio quality models:", error);
  }
}
